import React, { useState, useEffect } from "react";
import api, { riskPreventionApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  Camera,
  Plus,
  Clock,
  DollarSign,
  Check,
  X,
  AlertTriangle,
  FileText,
  History,
  Loader2,
  Send,
  User,
} from "lucide-react";

const CHANGE_TYPES = [
  { value: "scope_change", label: "Scope Change" },
  { value: "budget_change", label: "Budget Change" },
  { value: "timeline_change", label: "Timeline Change" },
  { value: "other", label: "Other Change" },
];

const ScopeChangeRequests = ({ projectId, serviceId = null, serviceName = null, isProjectActive = false, compact = false, onApplied = null }) => {
  const [changeRequests, setChangeRequests] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [applying, setApplying] = useState(false);
  
  const [newRequest, setNewRequest] = useState({
    description: "",
    change_type: "scope_change",
    time_impact_days: 0,
    cost_impact: 0,
  });

  useEffect(() => {
    fetchData();
  }, [projectId, serviceId]);

  const fetchData = async () => {
    try {
      const [requestsRes, snapshotsRes] = await Promise.all([
        riskPreventionApi.getChangeRequests(projectId, serviceId),
        !serviceId ? riskPreventionApi.getScopeSnapshots(projectId) : Promise.resolve({ data: [] }),
      ]);
      setChangeRequests(requestsRes.data);
      setSnapshots(snapshotsRes.data);
    } catch (error) {
      console.error("Error fetching scope data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!newRequest.description.trim()) {
      toast.error("Description is required");
      return;
    }

    setCreating(true);
    try {
      const requestData = {
        ...newRequest,
        service_id: serviceId, // Include service_id if provided
      };
      await riskPreventionApi.createChangeRequest(projectId, requestData);
      toast.success("Change request created");
      setDialogOpen(false);
      setNewRequest({
        description: "",
        change_type: "scope_change",
        time_impact_days: 0,
        cost_impact: 0,
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to create change request");
    } finally {
      setCreating(false);
    }
  };

  const handleApproveReject = async (requestId, status) => {
    try {
      await riskPreventionApi.updateChangeRequest(projectId, requestId, { status });
      toast.success(`Request ${status}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update request");
    }
  };

  const handleApplyToAmount = async () => {
    setApplying(true);
    try {
      const res = await riskPreventionApi.applyChangeRequestsToService(projectId, serviceId);
      const amt = res?.data?.amount;
      toast.success(`Payment amount updated to $${amt}`);
      fetchData();
      if (onApplied) onApplied();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply change requests");
    } finally {
      setApplying(false);
    }
  };

  const handleSendToClient = async (requestId) => {
    try {
      await api.post(`/projects/${projectId}/change-requests/${requestId}/send-to-client`);
      toast.success("Scope change sent to client for approval");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send to client");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-orange-500/10 text-orange-400 border-orange-500/30",
      approved: "bg-green-500/10 text-green-400 border-green-500/30",
      rejected: "bg-red-500/10 text-red-400 border-red-500/30",
      sent_to_client: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    };
    const labels = {
      pending: "Pending",
      approved: "Approved",
      rejected: "Rejected",
      sent_to_client: "Awaiting Client",
    };
    return (
      <Badge variant="outline" className={cn("text-xs", styles[status])}>
        {labels[status] || status}
      </Badge>
    );
  };

  const pendingCount = changeRequests.filter((r) => r.status === "pending").length;
  const approvedCostDelta = changeRequests
    .filter((r) => r.status === "approved")
    .reduce((sum, r) => sum + (Number(r.cost_impact) || 0), 0);
  const hasApprovedCost = changeRequests.some((r) => r.status === "approved" && Number(r.cost_impact) !== 0);
  const latestSnapshot = snapshots[0];

  // Compact mode for service-level requests
  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
            <span className="text-sm text-zinc-400">
              Requests{serviceName ? ` for ${serviceName}` : ""}
            </span>
            {pendingCount > 0 && (
              <Badge className="bg-orange-500 text-white text-xs h-5">{pendingCount}</Badge>
            )}
          </div>

          <div className="flex items-center gap-1">
          {serviceId && hasApprovedCost && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleApplyToAmount}
              disabled={applying}
              className="h-7 text-emerald-400 hover:bg-emerald-500/10"
              title="Recalculate payment amount = base + approved change requests"
            >
              {applying ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Check className="h-3 w-3 mr-1" />
              )}
              Apply {approvedCostDelta > 0 ? "+" : ""}${approvedCostDelta} to amount
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="ghost" className="h-7 text-orange-400 hover:bg-orange-500/10">
                <Plus className="h-3 w-3 mr-1" />
                Add
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-zinc-50">
                  Create Change Request{serviceName ? ` for ${serviceName}` : ""}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Change Type</Label>
                  <Select
                    value={newRequest.change_type}
                    onValueChange={(v) => setNewRequest((p) => ({ ...p, change_type: v }))}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {CHANGE_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value} className="text-zinc-300">
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Description *</Label>
                  <Textarea
                    value={newRequest.description}
                    onChange={(e) => setNewRequest((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Describe the change..."
                    className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[100px]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Time Impact (days)
                    </Label>
                    <Input
                      type="number"
                      value={newRequest.time_impact_days}
                      onChange={(e) =>
                        setNewRequest((p) => ({ ...p, time_impact_days: parseInt(e.target.value) || 0 }))
                      }
                      className="bg-zinc-800 border-zinc-700 text-zinc-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300 flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      Cost Impact ($)
                    </Label>
                    <Input
                      type="number"
                      value={newRequest.cost_impact}
                      onChange={(e) =>
                        setNewRequest((p) => ({ ...p, cost_impact: parseFloat(e.target.value) || 0 }))
                      }
                      className="bg-zinc-800 border-zinc-700 text-zinc-50"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="border-zinc-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateRequest}
                    disabled={creating}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Request"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-2">
            <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
          </div>
        ) : changeRequests.length === 0 ? (
          <p className="text-xs text-zinc-500">No change requests for this service</p>
        ) : (
          <div className="space-y-2">
            {changeRequests.map((request) => (
              <div
                key={request.id}
                className={cn(
                  "p-2 rounded-lg border text-xs",
                  request.status === "pending"
                    ? "bg-orange-500/5 border-orange-500/20"
                    : request.status === "approved"
                    ? "bg-green-500/5 border-green-500/20"
                    : request.status === "sent_to_client"
                    ? "bg-blue-500/5 border-blue-500/20"
                    : "bg-zinc-800/50 border-zinc-700"
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getStatusBadge(request.status)}
                      <Badge variant="outline" className="text-[10px] h-4 bg-zinc-800 text-zinc-400 border-zinc-700">
                        {CHANGE_TYPES.find((t) => t.value === request.change_type)?.label || request.change_type}
                      </Badge>
                    </div>
                    <p className="text-zinc-300 line-clamp-2">{request.description}</p>
                    <div className="flex items-center gap-3 mt-1 text-zinc-500">
                      {request.time_impact_days !== 0 && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-2.5 w-2.5" />
                          {request.time_impact_days > 0 ? "+" : ""}{request.time_impact_days}d
                        </span>
                      )}
                      {request.cost_impact !== 0 && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-2.5 w-2.5" />
                          {request.cost_impact > 0 ? "+" : ""}${request.cost_impact}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1">
                    {request.status === "pending" && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSendToClient(request.id)}
                          className="h-6 px-1.5 text-blue-400 hover:bg-blue-500/10"
                          title="Send to client"
                        >
                          <Send className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleApproveReject(request.id, "approved")}
                          className="h-6 w-6 p-0 text-green-400 hover:bg-green-500/10"
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleApproveReject(request.id, "rejected")}
                          className="h-6 w-6 p-0 text-red-400 hover:bg-red-500/10"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Scope Snapshot Card - only show if not filtered by service - Compact */}
      {!serviceId && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-1 pt-2 px-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-zinc-50 font-['Manrope'] flex items-center gap-2 text-sm">
                <Camera className="h-4 w-4 text-purple-500" />
                Scope Snapshot
              </CardTitle>
              {latestSnapshot && (
                <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/30 text-xs px-1.5 py-0">
                  v{latestSnapshot.version}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-1 pb-2 px-3">
            {latestSnapshot ? (
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-zinc-400">
                    {new Date(latestSnapshot.created_at).toLocaleDateString()}
                  </span>
                  <span className="text-zinc-500">
                    {latestSnapshot.input_files?.length || 0} files
                  </span>
                  <span className="text-zinc-500">
                    {latestSnapshot.tasks?.length || 0} tasks
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-zinc-400 hover:text-zinc-50"
                  onClick={() => setSnapshotDialogOpen(true)}
                >
                  <History className="h-3 w-3 mr-1" />
                  History ({snapshots.length})
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3 py-2 text-zinc-500">
                <Camera className="h-5 w-5 opacity-50" />
                <div>
                  <p className="text-xs">No scope snapshot yet</p>
                  <p className="text-[10px] text-zinc-600">Snapshot will be created when project starts</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Change Requests Card - More Compact */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2 pt-2 px-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-zinc-50 font-['Manrope'] flex items-center gap-2 text-sm">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              Change Requests
              {pendingCount > 0 && (
                <Badge className="bg-orange-500 text-white text-xs px-1.5 py-0">{pendingCount}</Badge>
              )}
            </CardTitle>
            
            {isProjectActive && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-orange-500 hover:bg-orange-600 h-7 text-xs">
                    <Plus className="h-3 w-3 mr-1" />
                    New Request
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                  <DialogHeader>
                    <DialogTitle className="text-zinc-50">Create Change Request</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Change Type</Label>
                      <Select
                        value={newRequest.change_type}
                        onValueChange={(v) => setNewRequest((p) => ({ ...p, change_type: v }))}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {CHANGE_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value} className="text-zinc-300">
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-zinc-300">Description *</Label>
                      <Textarea
                        value={newRequest.description}
                        onChange={(e) => setNewRequest((p) => ({ ...p, description: e.target.value }))}
                        placeholder="Describe the change..."
                        className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[100px]"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Time Impact (days)
                        </Label>
                        <Input
                          type="number"
                          value={newRequest.time_impact_days}
                          onChange={(e) =>
                            setNewRequest((p) => ({ ...p, time_impact_days: parseInt(e.target.value) || 0 }))
                          }
                          className="bg-zinc-800 border-zinc-700 text-zinc-50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300 flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          Cost Impact ($)
                        </Label>
                        <Input
                          type="number"
                          value={newRequest.cost_impact}
                          onChange={(e) =>
                            setNewRequest((p) => ({ ...p, cost_impact: parseFloat(e.target.value) || 0 }))
                          }
                          className="bg-zinc-800 border-zinc-700 text-zinc-50"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setDialogOpen(false)}
                        className="border-zinc-700"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleCreateRequest}
                        disabled={creating}
                        className="bg-orange-500 hover:bg-orange-600"
                      >
                        {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Request"}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
            </div>
          ) : changeRequests.length === 0 ? (
            <div className="text-center py-4 text-zinc-500">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No change requests</p>
            </div>
          ) : (
            <div className="space-y-3">
              {changeRequests.map((request) => (
                <div
                  key={request.id}
                  className={cn(
                    "p-3 rounded-lg border",
                    request.status === "pending"
                      ? "bg-orange-500/5 border-orange-500/20"
                      : request.status === "approved"
                      ? "bg-green-500/5 border-green-500/20"
                      : request.status === "sent_to_client"
                      ? "bg-blue-500/5 border-blue-500/20"
                      : "bg-zinc-800/50 border-zinc-700"
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        {getStatusBadge(request.status)}
                        <Badge variant="outline" className="text-xs bg-zinc-800 text-zinc-400 border-zinc-700">
                          {CHANGE_TYPES.find((t) => t.value === request.change_type)?.label || request.change_type}
                        </Badge>
                        {/* Show service name if request is for a specific service */}
                        {request.service_name && (
                          <Badge variant="outline" className="text-xs bg-cyan-500/10 text-cyan-400 border-cyan-500/30">
                            {request.service_name}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-zinc-300">{request.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                        {request.time_impact_days !== 0 && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {request.time_impact_days > 0 ? "+" : ""}{request.time_impact_days} days
                          </span>
                        )}
                        {request.cost_impact !== 0 && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3" />
                            {request.cost_impact > 0 ? "+" : ""}${request.cost_impact}
                          </span>
                        )}
                        <span>by {request.created_by_name}</span>
                      </div>
                      {/* Show client info if sent to client */}
                      {request.status === "sent_to_client" && (
                        <div className="mt-2 text-xs text-blue-400 flex items-center gap-1">
                          <User className="h-3 w-3" />
                          Waiting for client approval...
                        </div>
                      )}
                      {request.client_response && (
                        <div className={cn(
                          "mt-2 text-xs flex items-center gap-1",
                          request.client_response === "approved" ? "text-green-400" : "text-red-400"
                        )}>
                          <User className="h-3 w-3" />
                          Client {request.client_response}
                          {request.client_response_date && ` on ${new Date(request.client_response_date).toLocaleDateString()}`}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1">
                      {/* Send to Client button - only for pending */}
                      {request.status === "pending" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSendToClient(request.id)}
                          className="h-7 px-2 text-blue-400 hover:bg-blue-500/10"
                          title="Send to client for approval"
                        >
                          <Send className="h-4 w-4 mr-1" />
                          <span className="text-xs">Client</span>
                        </Button>
                      )}
                      
                      {/* Internal approve/reject - only for pending */}
                      {request.status === "pending" && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleApproveReject(request.id, "approved")}
                            className="h-7 w-7 p-0 text-green-400 hover:bg-green-500/10"
                            title="Approve internally"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleApproveReject(request.id, "rejected")}
                            className="h-7 w-7 p-0 text-red-400 hover:bg-red-500/10"
                            title="Reject"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {request.status !== "pending" && request.approved_by_name && (
                    <p className="text-xs text-zinc-500 mt-2">
                      {request.status} by {request.approved_by_name} on{" "}
                      {new Date(request.approved_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Snapshots Dialog */}
      <Dialog open={snapshotDialogOpen} onOpenChange={setSnapshotDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Scope Snapshot History</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4 max-h-[400px] overflow-y-auto">
            {snapshots.map((snapshot) => (
              <div
                key={snapshot.id}
                className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700"
              >
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/30">
                    v{snapshot.version}
                  </Badge>
                  <span className="text-xs text-zinc-500">
                    {new Date(snapshot.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-zinc-400">
                    Input Files: <span className="text-zinc-300">{snapshot.input_files?.length || 0}</span>
                  </div>
                  <div className="text-zinc-400">
                    Tasks: <span className="text-zinc-300">{snapshot.tasks?.length || 0}</span>
                  </div>
                </div>
                {snapshot.notes && (
                  <p className="text-xs text-zinc-500 mt-2">{snapshot.notes}</p>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScopeChangeRequests;
